import { Controller } from '@hotwired/stimulus'

const BREAKPOINTS = [
  { minWidth: 1000, items: 5 },
  { minWidth: 750, items: 4 },
  { minWidth: 500, items: 3 },
  { minWidth: 0, items: 2 }
]

const DRAG_DISTANCE_THRESHOLD = 20
const DRAG_VELOCITY_THRESHOLD = 5
const RUBBER_BAND_FACTOR = 0.3

export default class extends Controller {
  static targets = ['trackOuter', 'track', 'line', 'arrowPrev', 'arrowNext', 'dots', 'mobileLine']

  connect () {
    this.position = 0
    this.visibleItems = 5
    this.itemWidth = 0
    this.firstLoadDone = false

    this.items = Array.from(this.trackTarget.querySelectorAll('.rnk-Timeline_Item'))
    this.totalItems = this.items.length

    this.recalcLayout()
    window.addEventListener('resize', this.handleResize)
    this.bindDrag()

    this.mobileItems = Array.from(this.element.querySelectorAll('.rnk-Timeline_MobileItem'))
    if (this.mobileItems.length > 0) {
      this.positionMobileLine()
      this.checkMobileScroll()
      window.addEventListener('scroll', this.checkMobileScroll, { passive: true })
    }
  }

  disconnect () {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.checkMobileScroll)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseup', this.handleMouseUp)
  }

  // ─── Desktop carousel ───
  recalcLayout = () => {
    const availableWidth = this.trackOuterTarget.clientWidth
    const breakpoint = BREAKPOINTS.find(b => availableWidth >= b.minWidth) || BREAKPOINTS[BREAKPOINTS.length - 1]
    this.visibleItems = Math.min(breakpoint.items, this.totalItems)
    this.itemWidth = availableWidth / this.visibleItems

    this.items.forEach(el => { el.style.flex = `0 0 ${this.itemWidth}px` })

    this.position = Math.min(this.position, this.maxPosition)
    this.positionLine()
    this.update()
  }

  handleResize = () => this.recalcLayout()

  positionLine () {
    if (this.items.length < 2) return

    const first = this.items[0]
    const last = this.items[this.items.length - 1]
    const left = first.offsetLeft + first.offsetWidth / 2
    const right = last.offsetLeft + last.offsetWidth / 2

    this.lineTarget.style.left = `${left}px`
    this.lineTarget.style.width = `${right - left}px`
  }

  next () { this.goTo(this.position + 1) }
  prev () { this.goTo(this.position - 1) }

  goTo (newPosition) {
    this.position = Math.max(0, Math.min(newPosition, this.maxPosition))
    this.update()
  }

  goToIndex (event) {
    this.goTo(parseInt(event.currentTarget.dataset.index, 10))
  }

  update () {
    this.trackTarget.style.transform = `translateX(-${this.position * this.itemWidth}px)`
    this.arrowPrevTarget.classList.toggle('is-disabled', this.position === 0)
    this.arrowNextTarget.classList.toggle('is-disabled', this.position >= this.maxPosition)
    this.animateVisibleCards()
    this.renderDots()
  }

  animateVisibleCards () {
    const start = this.position
    const end = Math.min(this.position + this.visibleItems, this.totalItems)

    if (!this.firstLoadDone) {
      for (let i = start; i < end; i++) {
        const delay = (i - start) * 80
        setTimeout(() => this.items[i].classList.add('is-visible'), delay)
      }
      this.firstLoadDone = true
      return
    }

    this.items.forEach((item, i) => {
      const visible = i >= start && i < end
      item.classList.toggle('is-visible', visible)
    })
  }

  renderDots () {
    const dotAction = 'click->timeline-carousel#goToIndex'
    const dots = []

    dots.push(this.navLabelHtml('goToStart', 'Inicio'))
    for (let i = 0; i < this.maxPosition + 1; i++) {
      const activeClass = i === this.position ? ' is-active' : ''
      const label = `Ir al hito ${i + 1}`
      dots.push(
        `<button class="rnk-Timeline_DotNav${activeClass}" type="button"` +
        ` aria-label="${label}" data-index="${i}" data-action="${dotAction}"></button>`
      )
    }
    dots.push(this.navLabelHtml('goToEnd', 'Final'))

    this.dotsTarget.innerHTML = dots.join('')
  }

  navLabelHtml (method, label) {
    return `<button class="rnk-Timeline_NavLabel" type="button"` +
      ` data-action="click->timeline-carousel#${method}">${label}</button>`
  }

  goToStart () { this.goTo(0) }
  goToEnd () { this.goTo(this.maxPosition) }

  get maxPosition () {
    return Math.max(0, this.totalItems - this.visibleItems)
  }

  // ─── Drag/swipe ───
  handleMouseDown = e => { e.preventDefault(); this.dragStart(e.clientX) }
  handleMouseMove = e => this.dragMove(e.clientX)
  handleMouseUp = () => this.dragEnd()
  handleTouchStart = e => this.dragStart(e.touches[0].clientX)
  handleTouchMove = e => this.dragMove(e.touches[0].clientX)
  handleTouchEnd = () => this.dragEnd()

  bindDrag () {
    this.dragState = { active: false, startX: 0, lastX: 0, dragOffset: 0, velocity: 0 }

    this.trackOuterTarget.addEventListener('mousedown', this.handleMouseDown)
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('mouseup', this.handleMouseUp)

    this.trackOuterTarget.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.trackOuterTarget.addEventListener('touchmove', this.handleTouchMove, { passive: true })
    this.trackOuterTarget.addEventListener('touchend', this.handleTouchEnd)
  }

  dragStart (x) {
    this.dragState.active = true
    this.dragState.startX = x
    this.dragState.lastX = x
    this.dragState.dragOffset = 0
    this.dragState.velocity = 0
    this.trackTarget.classList.add('no-transition')
    this.trackOuterTarget.classList.add('is-dragging')
  }

  dragMove (x) {
    if (!this.dragState.active) return

    this.dragState.velocity = x - this.dragState.lastX
    this.dragState.lastX = x
    this.dragState.dragOffset = x - this.dragState.startX

    const base = this.position * this.itemWidth
    let offset = this.dragState.dragOffset

    const atLeftEdge = this.position === 0 && offset > 0
    const atRightEdge = this.position >= this.maxPosition && offset < 0
    if (atLeftEdge || atRightEdge) offset *= RUBBER_BAND_FACTOR

    this.trackTarget.style.transform = `translateX(-${base - offset}px)`
  }

  dragEnd () {
    if (!this.dragState.active) return
    this.dragState.active = false
    this.trackTarget.classList.remove('no-transition')
    this.trackOuterTarget.classList.remove('is-dragging')

    const distance = Math.abs(this.dragState.dragOffset)
    const speed = Math.abs(this.dragState.velocity)
    let steps = 0

    if (distance > DRAG_DISTANCE_THRESHOLD || speed > DRAG_VELOCITY_THRESHOLD) {
      steps = Math.max(1, Math.round((distance + speed * 3) / this.itemWidth))
      steps = Math.min(steps, this.visibleItems)
    }

    if (steps > 0) {
      const direction = this.dragState.dragOffset < 0 ? 1 : -1
      this.goTo(this.position + direction * steps)
    } else {
      this.update()
    }
  }

  // ─── Mobile vertical timeline ───
  positionMobileLine () {
    if (this.mobileItems.length < 2 || !this.hasMobileLineTarget) return

    const first = this.mobileItems[0].querySelector('.rnk-Timeline_MobileDot')
    const last = this.mobileItems[this.mobileItems.length - 1].querySelector('.rnk-Timeline_MobileDot')
    if (!first || !last) return

    const containerTop = this.element.querySelector('.rnk-Timeline_Mobile').getBoundingClientRect().top
    const top = first.getBoundingClientRect().top - containerTop + first.offsetHeight / 2
    const bottom = last.getBoundingClientRect().top - containerTop + last.offsetHeight / 2

    this.mobileLineTarget.style.top = `${top}px`
    this.mobileLineTarget.style.height = `${bottom - top}px`
  }

  checkMobileScroll = () => {
    const viewportBottom = window.innerHeight
    this.mobileItems.forEach(el => {
      if (el.getBoundingClientRect().top < viewportBottom - 40) {
        el.classList.add('is-visible')
      }
    })
  }
}
